<template>
    <div ref="twoFactorBlock" class="card mb-3">
        <div class="card-header">
            <h5 class="card-title mb-0">Two Factor Authentication (2FA)</h5>
        </div>
        <div class="card-body">
            <div v-if="twoFactorMessage">
                <div class="alert alert-info alert-dismissible fade show" role="alert">
                    <span v-html="twoFactorMessage"></span>
                    <button type="button" class="btn-close" @click="(event) => twoFactorMessage = ''" data-bs-dismiss="alert" aria-label="Close"></button>
                </div>
            </div>
            <div class="mb-3 position-relative">
                <div class="form-check form-switch">
                    <input class="form-check-input" v-model="twoFactorVerified" type="checkbox" id="twoFactor" name="twoFactor" @change="toggleTwoFA">
                    <label class="form-check-label d-flex flex-column flex-sm-row align-items-start align-items-sm-center" for="twoFactor" >
                        <span v-html="twoFactorVerified ? 'Disable 2FA (Authenticator App)' : 'Enable 2FA via Authenticator App'"></span>

                        <span v-if="twoFactorVerified" class="d-flex align-items-center badge badge-success p-1 ms-2">
                            <i class="me-2 bi bi-check-circle p-0 h-auto w-auto" style="font-size: 16px;"></i> <span>Activated</span>
                        </span>
                        <span v-else class="d-flex align-items-center badge badge-danger p-1 ms-2">
                            <i class="me-2 bi bi-exclamation-triangle p-0 h-auto w-auto" style="font-size: 16px;"></i> <span>Disabled</span>
                        </span>

                    </label>
                </div>

                <p class="text-muted small" v-if="!twoFactorVerified">
                    You will need to use Authenticator App like Google Authenticator, Microsoft Authenticator, Authy, etc. to scan the QR Code. Then use the code generated by the app to login and other 2FA Actions.
                </p>
                <p class="text-muted small" v-else>
	                Disable Two Factor Authentication which is being done via Authenticator App. Email based 2FA will still be active.
                </p>
            </div>
            <div v-if="formErrors.two_fa_status" class="validation-error mx-1 mb-2" v-html="formErrors.two_fa_status"></div>

            <div v-if="twoFactorVerified" class="mb-3">
                <button type="button" class="btn btn-outline-dark rounded-pill py-2 px-3 mb-1" @click="viewQRCode" v-ripple>View QR Code</button>
                <div class="text-muted ms-2 small">
                    View QR Code to link with the Authenticator App.
                </div>
            </div>

        </div>
    </div>
	<AuthenticatedActionModal :action="authAction" :consider-trusted-device="considerTrustedDevice" :can-switch-two-factor-method="switchTwoFactorMethods" :can-use-recovery-codes="useRecoveryCodes" :reload-user="reloadUser" v-if="showActionModal && authAction" @close-modal="closeAuthActionModal"></AuthenticatedActionModal>

</template>

<script setup>
import { onMounted, ref} from "vue";
import {useAuthStore} from "@/stores/auth-store.js";
import apiClient from "@/utils/axios.js";
import {useHttpErrorManager} from "@/composables/useHttpErrorManager.js";
import {useAppHelpers} from "@/composables/useAppHelpers.js";
import AuthenticatedActionModal from "@/components/GenericModalAndViews/AuthenticatedActionModal.vue";

const auth = useAuthStore();
const helper = useAppHelpers();

const formErrors = ref({});

const showActionModal = ref(false);
const considerTrustedDevice = ref(true);
const switchTwoFactorMethods = ref(false);
const useRecoveryCodes = ref(false);
const reloadUser = ref(true);
const authAction = ref('');

const closeAuthActionModal = async () => {
	await checkTwoFAStatus();

	authAction.value = '';
	reloadUser.value = true;
	showActionModal.value = false;
	considerTrustedDevice.value = true;
	switchTwoFactorMethods.value = false;
	useRecoveryCodes.value = false;
}

const startTwoFactorDisable = () => {
	authAction.value = 'two_factor_disable';
	considerTrustedDevice.value = false;
	switchTwoFactorMethods.value = true;
	useRecoveryCodes.value = true;
	showActionModal.value = true;
}

const startTwoFactorEnable = () => {
	authAction.value = 'two_factor_enable';
	considerTrustedDevice.value = false;
	switchTwoFactorMethods.value = false;
	useRecoveryCodes.value = false;
	showActionModal.value = true;
}

const viewQRCode = () => {
	authAction.value = 'two_factor_qr_code';
	considerTrustedDevice.value = true;
	switchTwoFactorMethods.value = false;
	useRecoveryCodes.value = false;
	reloadUser.value = false;
	showActionModal.value = true;
}

const twoFactorBlock = ref(null);
const twoFactorMessage = ref('');
const twoFactorType = ref('email');
const twoFactorVerified = ref(false);

onMounted(async () => {
    await checkTwoFAStatus();
});

const checkTwoFAStatus = async () => {
	twoFactorMessage.value = '';
	helper.loaderOn(twoFactorBlock.value);
	try {
		const response = await apiClient.post('/auth/user/two-factor-status');
		if (response.status === 200) {
			twoFactorType.value = response.data.two_factor_type;
			twoFactorVerified.value = response.data.two_factor_verified;
		}
		twoFactorMessage.value = '';
		helper.loaderOff(twoFactorBlock.value);
	} catch (error) {
		let errorObj = await useHttpErrorManager().handleError(error, false);
		helper.loaderOff(twoFactorBlock.value);
		twoFactorMessage.value = errorObj['full_message'];
	}
}

const toggleTwoFA = async () => {
	twoFactorVerified.value = !twoFactorVerified.value;
	if(twoFactorVerified.value) {
		startTwoFactorDisable();
	}
	else {
		startTwoFactorEnable();
	}
}


</script>

<style lang="scss" scoped>
.two-fa-url {
    white-space: pre-wrap;
    background: #eaeaea !important;
    padding-inline: 1rem;
    padding-block: 0.5rem;
}
</style>
